/**
 * "Convert to invoice" on an ACCEPTED Earthworks quote (fieldiq-api#1300,
 * EW-6, POST /earthworks/invoices/from-quote). Drawn inside EwQuotePanel, the
 * quote screen's Earthworks section, so the shared quote screen is not edited.
 *
 * The office bills the quote in full, or a progress draw of a % of every
 * quote line. The live draws of a quote add up to at most 100%; how much is
 * drawn and left is the API's own sum (GET /earthworks/quotes/:id/invoice-
 * draws), and the % input is bounded by it. The API's draw_exceeds_quote
 * still decides and, when it arrives, its remaining_pct replaces the figure
 * shown here.
 *
 * DOUBLE BILLING (Jason 2026-11-01: warn, don't refuse). When the quote's job
 * was already invoiced from field tickets or a change order, the draws read's
 * overlapNote is shown before Convert; Convert then answers 409
 * billing_overlap and "Confirm and invoice anyway" resends with
 * confirm_overlap: true.
 *
 * Admin / office staff only (the API's EW_ADMIN_ROLES).
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileOutput } from 'lucide-react';
import { useCreateEwInvoiceFromQuote, useEwQuoteDraws } from '../../hooks/useEwInvoices';
import { ewApiError, ewRefusal, fmtNumber } from '../../lib/earthworks';
import { canWriteEwInvoices, ewBillingOverlapWarning, ewDrawPctVerdict, ewInvoiceStatusSkin } from '../../lib/ew-invoices';
import { EwOverlapConfirm, EwOverlapNote } from '../../pages/earthworks/invoices/EwOverlapConfirm';
import { useAuth } from '../../contexts/AuthContext';
import { queryLoadFailure } from '../../lib/load-failure';

const INPUT = 'bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-sm text-white';
const BTN_PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:opacity-50 disabled:cursor-not-allowed';

export function EwQuoteInvoiceSection({ quoteId }: { quoteId: string; quoteNumber?: string }) {
  const { user } = useAuth();
  const canWrite = canWriteEwInvoices(user?.role);
  const navigate = useNavigate();
  const draws = useEwQuoteDraws(quoteId);
  const create = useCreateEwInvoiceFromQuote();
  const [mode, setMode] = useState<'full' | 'draw'>('full');
  const [pctText, setPctText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [apiRemaining, setApiRemaining] = useState<number | null>(null);
  const [overlap, setOverlap] = useState<string | null>(null);

  const derived = queryLoadFailure(draws) || !draws.data ? null : draws.data;
  const remaining = apiRemaining ?? derived?.remainingPct ?? null;
  const known = remaining !== null;
  const nothingLeft = known && remaining <= 0;
  const verdict = mode === 'full' ? ewDrawPctVerdict('100', remaining) : ewDrawPctVerdict(pctText, remaining);
  const verdictError = 'error' in verdict ? verdict.error : null;
  const fullBlocked = mode === 'full' && known && remaining < 100;

  const go = (confirm = false) => {
    setError(null);
    setOverlap(null);
    if ('error' in verdict) return setError(verdict.error);
    create.mutate({ quote_id: quoteId, ...(verdict.pct === 100 ? {} : { pct: verdict.pct }), ...(confirm ? { confirm_overlap: true } : {}) }, {
      onSuccess: (inv) => {
        if (inv?.id) navigate(`/earthworks/invoices/${inv.id}`);
      },
      onError: (err) => {
        const e = ewApiError(err);
        if (e.code === 'draw_exceeds_quote' && typeof e.extra.remaining_pct === 'number') setApiRemaining(e.extra.remaining_pct);
        const warning = ewBillingOverlapWarning(err);
        if (warning) setOverlap(warning);
        else setError(ewRefusal(err, 'The quote could not be converted to an invoice.'));
      },
    });
  };

  return (
    <div className="border-t border-fieldiq-border pt-3" data-testid="ew-quote-invoice">
      <h3 className="mb-2 text-sm font-semibold text-white">Invoice</h3>
      <p className="text-xs text-fieldiq-muted" data-testid="ew-quote-draw-remaining">
        {known
          ? `${fmtNumber(apiRemaining !== null ? 100 - apiRemaining : (derived as NonNullable<typeof derived>).drawnPct)}% of this quote is invoiced · ${fmtNumber(remaining)}% left`
          : draws.isLoading
            ? 'Checking how much of this quote is invoiced…'
            : 'How much of this quote is invoiced could not be read.'}
      </p>
      {derived && derived.invoices.some((r) => r.live) && (
        <ul className="mt-1 text-xs text-fieldiq-muted" data-testid="ew-quote-draws">
          {derived.invoices.filter((r) => r.live).map((r) => (
            <li key={r.id}>
              <Link className="font-mono text-white underline" to={`/earthworks/invoices/${r.id}`}>{r.invoiceNumber}</Link>
              {` · ${fmtNumber(r.quotePct)}% · ${ewInvoiceStatusSkin(r.status).label}`}
            </li>
          ))}
        </ul>
      )}

      {derived?.overlapNote && (
        <div className="mt-2"><EwOverlapNote text={derived.overlapNote} testId="ew-quote-overlap-note" /></div>
      )}
      {canWrite && !nothingLeft && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="ew-quote-convert-form">
          <select aria-label="Invoice how much" value={mode} onChange={(e) => { setMode(e.target.value as 'full' | 'draw'); setError(null); }} className={INPUT}>
            <option value="full">The whole quote</option>
            <option value="draw">A progress draw (%)</option>
          </select>
          {mode === 'draw' && (
            <input
              aria-label="Draw percent"
              inputMode="decimal"
              type="number"
              min={0.01}
              step={0.01}
              max={remaining ?? 100}
              value={pctText}
              onChange={(e) => setPctText(e.target.value)}
              placeholder={known ? `up to ${fmtNumber(remaining)}` : 'e.g. 30'}
              className={`${INPUT} w-28`}
            />
          )}
          <button
            type="button"
            className={BTN_PRIMARY}
            disabled={create.isPending || !known || !!verdictError}
            onClick={() => go()}
            data-testid="ew-quote-convert"
          >
            <FileOutput size={14} />
            {create.isPending ? 'Converting…' : 'Convert to invoice'}
          </button>
        </div>
      )}
      {canWrite && fullBlocked && !nothingLeft && (
        <p className="mt-2 text-xs text-fieldiq-muted">Part of this quote is already invoiced, so the rest goes on as a progress draw.</p>
      )}
      {canWrite && mode === 'draw' && verdictError && pctText.trim() !== '' && (
        <p className="mt-2 text-xs text-fi-signal-amber" data-testid="ew-quote-draw-error">{verdictError}</p>
      )}
      {canWrite && nothingLeft && <p className="mt-2 text-xs text-fieldiq-muted">The whole quote is invoiced.</p>}
      {overlap && (
        <div className="mt-2">
          <EwOverlapConfirm message={overlap} pending={create.isPending} onConfirm={() => go(true)} onCancel={() => setOverlap(null)} />
        </div>
      )}
      {error && <p role="alert" className="mt-2 text-sm text-fieldiq-text" data-testid="ew-quote-convert-error">{error}</p>}
    </div>
  );
}
